import React, { useState, useEffect, useRef } from 'react';
import { goalEarnedTotal, goalAvailablePointsFallback } from '../utils/goalPoints';
import useDealerData from './dealer/useDealerData';
import useDealerRewards from './dealer/useDealerRewards';
import MatchPredictorSection from './dealer/MatchPredictorSection';
import PastGamesSection from './dealer/PastGamesSection';
import RewardsSection from './dealer/RewardsSection';
import {
  Box,
  Drawer,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  useMediaQuery,
  useTheme,
  Container,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Button,
  CardActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  LinearProgress,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  Leaderboard as LeaderboardIcon,
  Quiz as QuizIcon,
  Assignment as BonusTaskIcon,
  TrendingUp as MarketingIcon,
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  CreditCard as CreditCardIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PasswordChangeDialog from '../components/PasswordChangeDialog';
import FormalDocumentsAcceptanceDialog from '../components/FormalDocumentsAcceptanceDialog';
import LeaderboardsSection from '../components/LeaderboardsSection';
import RichTextEditor from '../components/RichTextEditor';
import { formatCurrency } from '../utils/currency';
import { useTranslation, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../translations/dealer';
import dayjs from 'dayjs';
import { getQuizPreviewHtml, getQuizPointsAwarded, getQuizRewardLabel, quizNoTranslateProps, quizRichContentSx, quizRichHtmlBlockSx, shouldRevealCorrectAnswers } from '../utils/quizDisplay';
import { isTournamentCampaign } from '../utils/tournamentCampaign';
import {
  getTournamentRewardsLockedReason,
} from '../utils/tournamentRewards';

/** Options shown to dealers: A/B always; C/D only when non-empty. */
/** Info banner: quiz points are credited only after recorded sales (API: dealer_has_sale_values). */
function showQuizSalesRewardDisclaimer(quiz) {
  return Boolean(quiz?.only_with_points && !quiz?.dealer_has_sale_values);
}

const pointsLockedAlertSx = {
  mb: 2,
  color: 'text.primary',
  bgcolor: 'rgba(25, 118, 210, 0.09)',
  '& .MuiAlert-message': {
    color: 'text.primary',
  },
  '& .MuiAlert-icon': {
    color: 'info.main',
  },
};

function getVisibleQuizOptions(question) {
  const opts = [
    { key: 'A', text: question.answer_a },
    { key: 'B', text: question.answer_b },
  ];
  const c = (question.answer_c || '').trim();
  const d = (question.answer_d || '').trim();
  if (c) opts.push({ key: 'C', text: question.answer_c });
  if (d) opts.push({ key: 'D', text: question.answer_d });
  return opts;
}

const drawerWidth = 240;
const drawerMobileWidth = 64;

const Dealer = () => {
  const { user, token, logout, apiCall, loading, API_URL, CDN_URL, isImpersonating, originalUser, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('dealerSidebarCollapsed') === 'true');
  const isNarrow = isMobile || sidebarCollapsed;
  const currentDrawerWidth = isNarrow ? drawerMobileWidth : drawerWidth;
  const [selectedTab, setSelectedTab] = useState('dashboard');

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('dealerSidebarCollapsed', String(next));
      return next;
    });
  };
  // ── Data loading hook ──
  const dealerData = useDealerData({ user, loading, apiCall, CDN_URL, selectedTab, t: null });
  const {
    companyBrandingImage, companyBrandingColor, companyLanguage, companyTimezone, companySupportEmail,
    companyDashboardWelcome,
    companyCountryLoaded, companyIsRomania, companyIsSlovakia,
    companyExtraModules,
    campaigns, loadingCampaigns, campaignsError, campaignsPage, setCampaignsPage,
    campaignsRowsPerPage, setCampaignsRowsPerPage, campaignsTotal,
    viewingCampaignDetail, setViewingCampaignDetail,
    selectedCampaignDetail, setSelectedCampaignDetail,
    loadingCampaignDetail, campaignDetailError, setCampaignDetailError,
    campaignGoals, setCampaignGoals, loadingGoals, goalsError, setGoalsError,
    campaignGoalSettings,
    campaignQuizzes, setCampaignQuizzes, loadingCampaignQuizzes, campaignQuizzesError, setCampaignQuizzesError,
    marketingItems, loadingMarketingItems, marketingItemsError,
    dashboardCampaigns, loadingDashboardCampaigns,
    dashboardMarketingItems, loadingDashboardMarketing,
    campaignsWithLeaderboards, leaderboardGoals, loadingLeaderboardGoals, loadingLeaderboardCampaigns,
    leaderboardSort, handleLeaderboardSortChange, getSortedLeaderboardGoals,
    quizzes, loadingQuizzes, quizzesError, loadQuizzes,
    bonusTasks, loadingBonusTasks, bonusTasksError, loadBonusTasks,
    showFormalDocumentsDialog, setShowFormalDocumentsDialog,
    formalDocumentsData, loadingFormalDocumentsCheck,
    loadCampaignDetail, loadCampaignGoals, loadCampaignQuizzes,
    loadDealerCampaigns,
  } = dealerData;

  // Password change dialog state
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);

  // Quiz UI state (kept in Dealer since QuizView is still inline)
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [takingQuiz, setTakingQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [showingResults, setShowingResults] = useState(false);

  // Bonus task UI state
  const [selectedBonusTask, setSelectedBonusTask] = useState(null);
  const [bonusTaskAnswer, setBonusTaskAnswer] = useState('');
  const [bonusTaskFormError, setBonusTaskFormError] = useState('');
  const [submittingBonusTask, setSubmittingBonusTask] = useState(false);
  const t = useTranslation(companyLanguage);
  const dateLocale = companyLanguage === 'pl' ? 'pl-PL' : companyLanguage === 'sk' ? 'sk-SK' : 'en-US';
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (companyLanguage === 'pl') return dayjs(dateStr).format('YYYY-MM-DD');
    return new Date(dateStr).toLocaleDateString(dateLocale);
  };
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    if (companyLanguage === 'pl') return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
    return new Date(dateStr).toLocaleString(dateLocale);
  };
  const hasSSO = Boolean(process.env.REACT_APP_ENTRA_CLIENT_ID);
  const dealerPortalName = process.env.REACT_APP_DEALER_PORTAL_NAME || 'Dealer Portal';
  const dealerPortalUrl = process.env.REACT_APP_DEALER_PORTAL_URL;
  const hideDealerPortalByCountry =
    companyCountryLoaded && (companyIsRomania || companyIsSlovakia);
  const showDealerPortalSection = hasSSO && dealerPortalUrl && !hideDealerPortalByCountry;

  const menuItems = [
    { key: 'dashboard', label: t('menu.dashboard'), icon: DashboardIcon },
    { key: 'campaigns', label: t('menu.campaigns'), icon: CampaignIcon },
    ...(campaignsWithLeaderboards.length > 0 ? [{ key: 'leaderboards', label: t('menu.leaderboards'), icon: LeaderboardIcon }] : []),
    ...(bonusTasks.length > 0 ? [{ key: 'bonusTask', label: t('menu.bonusTask'), icon: BonusTaskIcon }] : []),
    ...(quizzes.length > 0 ? [{ key: 'quiz', label: t('menu.quiz'), icon: QuizIcon }] : []),
    { key: 'marketing', label: t('menu.marketing'), icon: MarketingIcon },
    ...(!hasSSO ? [{ key: 'settings', label: t('menu.settings'), icon: SettingsIcon }] : []),
    ...(showDealerPortalSection ? [{ key: 'dealerPortal', label: dealerPortalName, icon: OpenInNewIcon }] : []),
  ];

  // Match <html lang> to company locale so Chrome does not treat Polish UI as English.
  useEffect(() => {
    if (!companyCountryLoaded) return undefined;
    const lang = SUPPORTED_LANGUAGES.includes(companyLanguage) ? companyLanguage : DEFAULT_LANGUAGE;
    document.documentElement.setAttribute('lang', lang);
    return () => {
      document.documentElement.setAttribute('lang', DEFAULT_LANGUAGE);
    };
  }, [companyCountryLoaded, companyLanguage]);

  // Extra signal for Google Translate while a quiz is on screen.
  useEffect(() => {
    if (!takingQuiz && !showingResults) return undefined;
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'google');
    meta.setAttribute('content', 'notranslate');
    meta.setAttribute('data-smod-quiz-notranslate', '1');
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, [takingQuiz, showingResults]);

  // When SSO is configured, settings is hidden; reset selectedTab if it was settings
  useEffect(() => {
    if (hasSSO && selectedTab === 'settings') {
      setSelectedTab('dashboard');
    }
  }, [hasSSO, selectedTab]);

  // When no quizzes in active campaigns, redirect from quiz tab
  useEffect(() => {
    if (selectedTab === 'quiz' && !loadingQuizzes && quizzes.length === 0) {
      setSelectedTab('dashboard');
    }
  }, [selectedTab, loadingQuizzes, quizzes.length]);

  // When no active bonus tasks, redirect from bonus task tab
  useEffect(() => {
    if (selectedTab === 'bonusTask' && !loadingBonusTasks && bonusTasks.length === 0) {
      setSelectedTab('dashboard');
      setSelectedBonusTask(null);
    }
  }, [selectedTab, loadingBonusTasks, bonusTasks.length]);

  // Single active bonus task: open detail directly when entering the tab
  useEffect(() => {
    if (selectedTab !== 'bonusTask' || loadingBonusTasks) return;
    if (bonusTasks.length === 1 && !selectedBonusTask) {
      setSelectedBonusTask(bonusTasks[0]);
      setBonusTaskAnswer('');
      setBonusTaskFormError('');
    }
  }, [selectedTab, loadingBonusTasks, bonusTasks, selectedBonusTask]);

  // Keep selected bonus task in sync after reload (only when submission/status changes)
  useEffect(() => {
    if (!selectedBonusTask) return;
    const updated = bonusTasks.find((task) => task.id === selectedBonusTask.id);
    if (!updated) {
      if (!loadingBonusTasks) setSelectedBonusTask(null);
      return;
    }
    const prevSub = selectedBonusTask.latest_submission;
    const nextSub = updated.latest_submission;
    const changed =
      selectedBonusTask.can_submit !== updated.can_submit ||
      selectedBonusTask.title !== updated.title ||
      selectedBonusTask.description !== updated.description ||
      selectedBonusTask.points !== updated.points ||
      (prevSub?.id !== nextSub?.id) ||
      (prevSub?.status !== nextSub?.status) ||
      (prevSub?.answer !== nextSub?.answer);
    if (changed) {
      setSelectedBonusTask(updated);
    }
  }, [bonusTasks, loadingBonusTasks]);

  // ── Rewards / Cart hook ──
  const dealerRewards = useDealerRewards({
    user, apiCall, selectedCampaignDetail, campaignGoals, loadCampaignGoals, t
  });
  const {
    cart, cartDenominations, setCartDenominations,
    addToCart, removeFromCart, getCartTotal, clearCart,
    getTotalItemsInCart,
    edenredCart, edenredCustomValues, setEdenredCustomValues,
    clearEdenredCart, getEdenredCartTotal, getTotalItemsInEdenredCart,
    calculateEdenredTransferPoints,
    tremendousCustomValues, setTremendousCustomValues,
    requestingTremendousVoucher, calculateTremendousPoints,
    handleRequestTremendousVoucherClick,
    requestingVouchers, handleRequestVouchersClick, handleTransferMoneyClick,
    voucherItems, setVoucherItems, loadingVoucherItems, voucherItemsError, setVoucherItemsError,
    loadVoucherItems, loadPendingVoucherRequests,
    pendingVoucherRequests, setPendingVoucherRequests,
    showVoucherSuccessDialog, setShowVoucherSuccessDialog,
    showTremendousRewardSuccessDialog, setShowTremendousRewardSuccessDialog,
    tremendousRewardLink, setTremendousRewardLink,
    tremendousRewardInfo, setTremendousRewardInfo,
    showConfirmationDialog, setShowConfirmationDialog,
    confirmationPoints, confirmationAction, confirmationRewardId,
    handleConfirmedAction,
    feedbackDialog, closeFeedbackDialog,
    showFeedback,
    userRewardProviderConfig, setUserRewardProviderConfig, loadingUserConfig,
    selectedSerialNumber, setSelectedSerialNumber,
    requestingCard, setRequestingCard,
    getAvailablePoints, isEdenredReward, isTremendousReward,
    calculateCartSummaryPoints,
  } = dealerRewards;

  // NOTE: The following functions were extracted to useDealerData.js and useDealerRewards.js:
  // - fetchCompanyBrandingImage, loadCompanyExtraModules, loadDealerCampaigns
  // - loadCampaignsForLeaderboard, loadLeaderboardGoals, handleLeaderboardSortChange
  // - getSortedLeaderboardGoals, loadCampaignDetail, loadCampaignGoals
  // - loadCampaignQuizzes, loadMarketingItems, loadDashboardData, loadQuizzes
  // - getAvailablePoints, isEdenredReward, isTremendousReward
  // - getCartTotal, getEdenredCartTotal, addToCart, removeFromCart, clearCart
  // - calculateEdenredTransferPoints, calculateTremendousPoints
  // - handleRequestVouchers, handleRequestTremendousVoucher
  // - loadVoucherItems, loadPendingVoucherRequests
  const handleStartQuiz = (quiz) => {
    setCurrentQuiz(quiz);
    setTakingQuiz(true);
    setCurrentQuestionIndex(0);
    setQuizAnswers({});
    setShowingResults(false);
    setQuizResults(null);
    setSelectedTab('quiz');
  };

  const handleRetakeQuiz = (quiz) => {
    handleStartQuiz(quiz);
  };

  const handleAnswerSelect = (questionId, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!currentQuiz) return;

    setSubmittingQuiz(true);

    try {
      // Format answers for API
      const answers = currentQuiz.questions.map(question => ({
        question_id: question.id,
        selected_answer: quizAnswers[question.id]
      }));

      const result = await apiCall('post', '/api/quiz-attempts', {
        quiz_id: currentQuiz.id,
        answers: answers
      });

      if (result.success) {
        setQuizResults(result.data);
        setShowingResults(true);
        setTakingQuiz(false);
        // Reload quizzes to update completion status
        loadQuizzes();
      } else if (!result.requiresAuth) {
        alert(result.error || t('errors.failedSubmitQuiz'));
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert(t('errors.failedSubmitQuiz'));
    }

    setSubmittingQuiz(false);
  };

  const handleBackToQuizList = () => {
    setTakingQuiz(false);
    setShowingResults(false);
    setCurrentQuiz(null);
    setQuizAnswers({});
    setQuizResults(null);
    setCurrentQuestionIndex(0);
  };

  const isQuizCompleted = (quiz) => {
    return quiz.user_attempt && quiz.user_attempt.completed;
  };

  const isQuizPerfect = (quiz) => {
    return quiz.user_attempt &&
      quiz.user_attempt.completed &&
      quiz.user_attempt.score === quiz.user_attempt.total_questions;
  };

  const getQuizStatusColor = (quiz) => {
    if (!quiz.user_attempt) return 'default';
    if (isQuizPerfect(quiz)) return 'success';
    if (quiz.user_attempt.completed) return 'warning';
    return 'default';
  };

  const getQuizStatusText = (quiz) => {
    if (!quiz.user_attempt) return t('quiz.notStarted');
    if (isQuizPerfect(quiz)) return t('quiz.perfectScore');
    if (quiz.user_attempt.completed) return `${t('quiz.score')} ${quiz.user_attempt.score}/${quiz.user_attempt.total_questions}`;
    return t('quiz.inProgress');
  };

  const handleCampaignDetailOpen = (campaign) => {
    setViewingCampaignDetail(true);
    loadCampaignDetail(campaign.id);
    loadCampaignGoals(campaign.id);
    loadCampaignQuizzes(campaign.id);
    loadVoucherItems(campaign.id);
    loadPendingVoucherRequests(campaign.id);
  };

  const handleCampaignDetailClose = () => {
    setViewingCampaignDetail(false);
    setSelectedCampaignDetail(null);
    setCampaignDetailError('');
    setCampaignGoals([]);
    setGoalsError('');
    setCampaignQuizzes([]);
    setCampaignQuizzesError('');
    setVoucherItems([]);
    setVoucherItemsError('');
    setPendingVoucherRequests([]);
    clearCart();
  };

  const handleMenuItemClick = (tabKey) => {
    setSelectedTab(tabKey);
    if (tabKey === 'bonusTask') {
      if (bonusTasks.length === 1) {
        setSelectedBonusTask(bonusTasks[0]);
        setBonusTaskAnswer('');
        setBonusTaskFormError('');
      } else {
        setSelectedBonusTask(null);
        setBonusTaskAnswer('');
        setBonusTaskFormError('');
      }
    }
    // Reset campaign detail view when navigating
    if (viewingCampaignDetail) {
      setViewingCampaignDetail(false);
      setSelectedCampaignDetail(null);
      setCampaignDetailError('');
      setCampaignGoals([]);
      setGoalsError('');
      setCampaignQuizzes([]);
      setCampaignQuizzesError('');
      clearCart();
    }
  };

  const handleCampaignsPageChange = (event, newPage) => {
    setCampaignsPage(newPage);
  };

  const handleCampaignsRowsPerPageChange = (event) => {
    setCampaignsRowsPerPage(parseInt(event.target.value, 10));
    setCampaignsPage(0);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openBonusTask = (task) => {
    setSelectedBonusTask(task);
    setBonusTaskAnswer('');
    setBonusTaskFormError('');
  };

  const handleBackToBonusTaskList = () => {
    setSelectedBonusTask(null);
    setBonusTaskAnswer('');
    setBonusTaskFormError('');
  };

  const isBonusTaskAnswerEmpty = (html) => {
    if (!html || !String(html).trim()) return true;
    if (/<img\b/i.test(html)) return false;
    const text = String(html)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return !text;
  };

  const handleSubmitBonusTask = async () => {
    if (!selectedBonusTask) return;
    const answerHtml = bonusTaskAnswer || '';
    if (isBonusTaskAnswerEmpty(answerHtml)) {
      setBonusTaskFormError(t('bonusTask.answerRequired'));
      return;
    }
    setBonusTaskFormError('');
    setSubmittingBonusTask(true);
    try {
      const result = await apiCall('post', '/api/bonus-task-submissions', {
        bonus_task_id: selectedBonusTask.id,
        answer: answerHtml,
      });
      if (result.success) {
        setBonusTaskAnswer('');
        await loadBonusTasks();
      } else if (!result.requiresAuth) {
        setBonusTaskFormError(result.error || t('bonusTask.submitFailed'));
      }
    } catch (error) {
      console.error('Error submitting bonus task:', error);
      setBonusTaskFormError(t('bonusTask.submitFailed'));
    }
    setSubmittingBonusTask(false);
  };

  const bonusTaskStatusLabel = (status) => {
    if (status === 'PENDING') return t('bonusTask.statusPending');
    if (status === 'ACCEPTED') return t('bonusTask.statusAccepted');
    if (status === 'REJECTED') return t('bonusTask.statusRejected');
    return status;
  };

  const BonusTaskView = () => {
    if (loadingBonusTasks && bonusTasks.length === 0) {
      return (
        <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    if (selectedBonusTask) {
      const latest = selectedBonusTask.latest_submission;
      const canSubmit = selectedBonusTask.can_submit !== false;
      const showBack = bonusTasks.length > 1;

      return (
        <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {showBack && (
                <IconButton onClick={handleBackToBonusTaskList} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h5">{selectedBonusTask.title}</Typography>
            </Box>

            {bonusTasksError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bonusTasksError}
              </Alert>
            )}

            {selectedBonusTask.description && (
              <Box
                sx={{
                  ...quizRichContentSx,
                  mb: 2,
                  backgroundColor: '#ffffff',
                  color: 'text.primary',
                  '& p, & div, & span, & li': {
                    backgroundColor: 'transparent',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: selectedBonusTask.description }}
              />
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bonusTask.pointsLabel', { points: Number(selectedBonusTask.points) || 0 })}
            </Typography>

            {latest && (
              <Alert
                severity={
                  latest.status === 'ACCEPTED'
                    ? 'success'
                    : latest.status === 'REJECTED'
                      ? 'warning'
                      : 'info'
                }
                sx={{ mb: 2 }}
              >
                {t('bonusTask.latestStatus', { status: bonusTaskStatusLabel(latest.status) })}
                {latest.status === 'PENDING' && ` ${t('bonusTask.waitingReview')}`}
                {latest.status === 'ACCEPTED' &&
                  ` ${t('bonusTask.pointsAwarded', { points: Number(latest.points_awarded) || 0 })}`}
                {latest.status === 'REJECTED' && ` ${t('bonusTask.canResubmit')}`}
              </Alert>
            )}

            {latest && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('bonusTask.yourAnswer')}
                </Typography>
                <Box
                  sx={quizRichContentSx}
                  dangerouslySetInnerHTML={{ __html: latest.answer }}
                />
              </Box>
            )}

            {canSubmit ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('bonusTask.answerLabel')}
                </Typography>
                <RichTextEditor
                  simple
                  value={bonusTaskAnswer}
                  onChange={(value) => {
                    setBonusTaskAnswer(value);
                    if (bonusTaskFormError) setBonusTaskFormError('');
                  }}
                  placeholder={t('bonusTask.answerLabel')}
                  error={!!bonusTaskFormError}
                  style={{ marginBottom: 8 }}
                />
                {bonusTaskFormError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                    {bonusTaskFormError}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleSubmitBonusTask}
                  disabled={submittingBonusTask}
                  sx={{ mt: 1, backgroundColor: companyBrandingColor || '#1976d2' }}
                >
                  {submittingBonusTask ? t('bonusTask.submitting') : t('bonusTask.markDone')}
                </Button>
              </Box>
            ) : null}
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
        <Paper
          elevation={0}
          sx={{ p: 3, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}
        >
          <Typography variant="h5" gutterBottom>
            {t('bonusTask.availableTasks')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('bonusTask.availableTasksSubtitle')}
          </Typography>

          {bonusTasksError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bonusTasksError}
            </Alert>
          ) : bonusTasks.length === 0 ? (
            <Alert severity="info">{t('bonusTask.noTasksAvailable')}</Alert>
          ) : (
            <Grid container spacing={3}>
              {bonusTasks.map((task) => (
                <Grid item xs={12} md={6} lg={4} key={task.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {task.title}
                      </Typography>
                      {task.description && (
                        <Box
                          sx={{
                            ...quizRichContentSx,
                            mb: 1,
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                          dangerouslySetInnerHTML={{ __html: task.description }}
                        />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t('bonusTask.pointsLabel', { points: Number(task.points) || 0 })}
                      </Typography>
                      {task.latest_submission && (
                        <Chip
                          size="small"
                          sx={{ mt: 1 }}
                          label={bonusTaskStatusLabel(task.latest_submission.status)}
                          color={
                            task.latest_submission.status === 'ACCEPTED'
                              ? 'success'
                              : task.latest_submission.status === 'REJECTED'
                                ? 'warning'
                                : 'default'
                          }
                        />
                      )}
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => openBonusTask(task)}>
                        {t('bonusTask.openTask')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    );
  };

  // QuizView component definition
  const QuizView = () => {
    // If showing results
    if (showingResults && quizResults) {
      return (
        <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleBackToQuizList} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">
                {t('quiz.quizResults')}{' '}
                <Box component="span" {...quizNoTranslateProps}>
                  {currentQuiz?.title}
                </Box>
              </Typography>
            </Box>

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                {t('quiz.finalScore')} {quizResults.score} / {quizResults.total_questions}
              </Typography>
              {quizResults.score === quizResults.total_questions && !currentQuiz?.reward_per_question && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />
                  <Typography variant="body1" color="green">
                    {currentQuiz?.reward > 0 ? t('quiz.perfectScoreEarned', { points: currentQuiz.reward }) : `${t('quiz.perfectScore')}!`}
                  </Typography>
                </Box>
              )}
              {currentQuiz?.reward_per_question && quizResults.points_awarded > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />
                  <Typography variant="body1" color="green">
                    {t('quiz.scoreEarned', { points: quizResults.points_awarded })}
                  </Typography>
                </Box>
              )}
              {currentQuiz?.reward_per_question && quizResults.score === quizResults.total_questions && !(quizResults.points_awarded > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />
                  <Typography variant="body1" color="green">
                    {`${t('quiz.perfectScore')}!`}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="h6" gutterBottom>
              {t('quiz.questionReview')}
            </Typography>

            {(() => {
              const revealCorrectAnswers = quizResults.reveal_correct_answers
                ?? currentQuiz?.reveal_correct_answers
                ?? shouldRevealCorrectAnswers(currentQuiz, currentQuiz?.attempt_count);
              const reviewQuestions = quizResults.review_questions || currentQuiz?.review_questions;
              const reviewById = {};
              (reviewQuestions || []).forEach((q) => {
                reviewById[q.id] = q;
              });
              const getReviewQuestion = (question) => {
                if (!revealCorrectAnswers) return question;
                const review = reviewById[question.id];
                if (review) return { ...question, correct_answer: review.correct_answer };
                return question;
              };

              return currentQuiz?.questions.map((question, index) => {
              const reviewQuestion = getReviewQuestion(question);
              const userAnswer = quizResults.answers.find(a => a.question_id === question.id);
              const isCorrect = userAnswer?.is_correct;

              return (
                <Card key={question.id} sx={{ mb: 2, border: isCorrect ? '2px solid green' : '2px solid red' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2 }}>
                        {t('quiz.question', { num: index + 1 })}
                      </Typography>
                      {isCorrect ? (
                        <CheckCircleIcon sx={{ color: 'green' }} />
                      ) : (
                        <CancelIcon sx={{ color: 'red' }} />
                      )}
                    </Box>

                    <Box
                      component="div"
                      {...quizNoTranslateProps}
                      sx={{ mb: 2, color: 'text.primary', ...quizRichHtmlBlockSx }}
                      dangerouslySetInnerHTML={{ __html: question.question }}
                    />

                    <Grid container spacing={2}>
                      {getVisibleQuizOptions(question).map((opt) => {
                        const isSelected = userAnswer?.selected_answer === opt.key;
                        const isCorrectOption = revealCorrectAnswers
                          && String(reviewQuestion.correct_answer) === opt.key;

                        let backgroundColor = 'transparent';
                        let border = '1px solid #ddd';
                        if (revealCorrectAnswers) {
                          backgroundColor = isSelected
                            ? (String(reviewQuestion.correct_answer) === opt.key ? '#c8e6c9' : '#ffcdd2')
                            : (isCorrectOption ? '#c8e6c9' : 'transparent');
                          border = isCorrectOption ? '2px solid green' : '1px solid #ddd';
                        } else if (isSelected) {
                          backgroundColor = isCorrect ? '#c8e6c9' : '#ffcdd2';
                          border = isCorrect ? '2px solid green' : '2px solid red';
                        }

                        return (
                        <Grid item xs={12} sm={6} md={3} key={`${question.id}-${opt.key}`}>
                          <Box
                            {...quizNoTranslateProps}
                            sx={{
                            p: 1,
                            backgroundColor,
                            borderRadius: 1,
                            border
                          }}>
                            <Typography variant="body2">
                              <strong>{opt.key}:</strong> {opt.text}
                            </Typography>
                          </Box>
                        </Grid>
                        );
                      })}
                    </Grid>

                    {!isCorrect && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('quiz.yourAnswer', { answer: userAnswer?.selected_answer })}
                        {revealCorrectAnswers && reviewQuestion.correct_answer && (
                          <> | {t('quiz.correctAnswer', { answer: reviewQuestion.correct_answer })}</>
                        )}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            });
            })()}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleBackToQuizList}>
                {t('quiz.backToQuizList')}
              </Button>
              {quizResults.score !== quizResults.total_questions && (
                <Button variant="outlined" onClick={() => handleRetakeQuiz(currentQuiz)}>
                  {t('quiz.retakeQuiz')}
                </Button>
              )}
            </Box>
          </Paper>
        </Container>
      );
    }

    // If taking quiz
    if (takingQuiz && currentQuiz) {
      const currentQuestion = currentQuiz.questions[currentQuestionIndex];
      const isLastQuestion = currentQuestionIndex === currentQuiz.questions.length - 1;
      const selectedAnswer = quizAnswers[currentQuestion.id];

      return (
        <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleBackToQuizList} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" {...quizNoTranslateProps}>
                {currentQuiz.title}
              </Typography>
            </Box>

            {currentQuiz.description && (
              <Box
                component="div"
                {...quizNoTranslateProps}
                sx={{ color: 'text.secondary', mb: 3, ...quizRichHtmlBlockSx }}
                dangerouslySetInnerHTML={{ __html: currentQuiz.description }}
              />
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('quiz.questionOf', { current: currentQuestionIndex + 1, total: currentQuiz.questions.length })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(currentQuestionIndex + 1) / currentQuiz.questions.length * 100}
                sx={{ mt: 1 }}
              />
            </Box>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('quiz.question', { num: currentQuestionIndex + 1 })}
                </Typography>

                <Box
                  component="div"
                  {...quizNoTranslateProps}
                  sx={{ mb: 3, color: 'text.primary', ...quizRichHtmlBlockSx }}
                  dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
                />

                <FormControl component="fieldset">
                  <RadioGroup
                    value={selectedAnswer || ''}
                    onChange={(e) => handleAnswerSelect(currentQuestion.id, e.target.value)}
                  >
                    {getVisibleQuizOptions(currentQuestion).map((opt) => (
                      <FormControlLabel
                        key={`${currentQuestion.id}-${opt.key}`}
                        value={opt.key}
                        control={<Radio />}
                        label={
                          <Typography variant="body1" {...quizNoTranslateProps}>
                            <strong>{opt.key}:</strong> {opt.text}
                          </Typography>
                        }
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
                startIcon={<ArrowBackIcon />}
              >
                {t('quiz.previous')}
              </Button>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {currentQuiz.questions.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: index === currentQuestionIndex ?
                        (companyBrandingColor || '#1976d2') :
                        (quizAnswers[currentQuiz.questions[index].id] ? '#4caf50' : '#e0e0e0')
                    }}
                  />
                ))}
              </Box>

              {isLastQuestion ? (
                <Button
                  variant="contained"
                  onClick={handleSubmitQuiz}
                  disabled={!selectedAnswer || submittingQuiz}
                  endIcon={submittingQuiz ? <CircularProgress size={20} /> : <CheckIcon />}
                  sx={{ backgroundColor: companyBrandingColor || '#1976d2' }}
                >
                  {submittingQuiz ? t('quiz.submitting') : t('quiz.completeQuiz')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNextQuestion}
                  disabled={!selectedAnswer}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ backgroundColor: companyBrandingColor || '#1976d2' }}
                >
                  {t('quiz.next')}
                </Button>
              )}
            </Box>
          </Paper>
        </Container>
      );
    }

    // Quiz list view
    return (
      <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4, overflow: 'visible' }}>
        <Paper sx={{ p: 3, overflow: 'visible' }}>
          <Typography variant="h5" gutterBottom>
            {t('quiz.availableQuizzes')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('quiz.availableQuizzesSubtitle')}
          </Typography>

          {loadingQuizzes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : quizzesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {quizzesError}
            </Alert>
          ) : quizzes.length === 0 ? (
            <Alert severity="info">
              {t('quiz.noQuizzesAvailable')}
            </Alert>
          ) : (
            <Grid container spacing={3} sx={{ overflow: 'visible' }}>
              {quizzes.map((quiz) => {
                const singleQuiz = quizzes.length === 1;
                const salesRewardDisclaimer = showQuizSalesRewardDisclaimer(quiz);
                return (
                <Grid item xs={12} md={singleQuiz ? 12 : 6} lg={singleQuiz ? 12 : 4} key={quiz.id} sx={{ alignSelf: 'flex-start', width: '100%', overflow: 'visible' }}>
                  {salesRewardDisclaimer && (
                    <Alert severity="info" sx={pointsLockedAlertSx}>
                      {t('quiz.pointsRequiredDisclaimer')}
                    </Alert>
                  )}
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible',
                      width: '100%',
                    }}
                  >
                    <CardContent sx={{ flex: '1 1 auto', minHeight: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" component="h2" gutterBottom {...quizNoTranslateProps}>
                          {quiz.title}
                        </Typography>
                        <Chip
                          label={getQuizStatusText(quiz)}
                          color={getQuizStatusColor(quiz)}
                          size="small"
                        />
                      </Box>

                      {getQuizPreviewHtml(quiz) && (
                        <Box
                          {...quizNoTranslateProps}
                          sx={{ ...quizRichContentSx, mb: 2 }}
                          dangerouslySetInnerHTML={{ __html: getQuizPreviewHtml(quiz) }}
                        />
                      )}

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Questions: {quiz.questions?.length || 0}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Attempts: {quiz.attempt_count || 0}/3
                      </Typography>

                      {getQuizRewardLabel(quiz, t, 'quiz') && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {getQuizRewardLabel(quiz, t, 'quiz')}
                        </Typography>
                      )}


                      {quiz.user_attempt?.reward_awarded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <CheckCircleIcon sx={{ color: 'green', fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2" color="green">
                            {t('quiz.rewardClaimed', { points: getQuizPointsAwarded(quiz) })}
                          </Typography>
                        </Box>
                      )}

                      {(quiz.attempt_count >= 3 && !isQuizPerfect(quiz)) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography variant="body2" color="error">
                            {t('quiz.maxAttemptsReached')}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions sx={{ flexShrink: 0, px: 2, pb: 2, pt: 0 }}>
                      {!isQuizCompleted(quiz) ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleStartQuiz(quiz)}
                          disabled={quiz.attempt_count >= 3}
                          sx={{ backgroundColor: companyBrandingColor || '#1976d2' }}
                        >
                          {t('quiz.startQuiz')}
                        </Button>
                      ) : isQuizPerfect(quiz) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setQuizResults(quiz.user_attempt) || setShowingResults(true) || setCurrentQuiz(quiz)}
                        >
                          {t('quiz.viewResults')}
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleRetakeQuiz(quiz)}
                            disabled={quiz.attempt_count >= 3}
                            sx={{ backgroundColor: companyBrandingColor || '#1976d2' }}
                          >
                            Retake Quiz
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setQuizResults(quiz.user_attempt) || setShowingResults(true) || setCurrentQuiz(quiz)}
                          >
                            {t('quiz.viewResults')}
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      </Container>
    );
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'dashboard':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor: companyBrandingColor || '#1976d2',
                color: 'white',
                mb: 4
              }}
            >
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, fontSize: '2.4rem' }}>
                {companyDashboardWelcome || t('dashboard.welcome')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
                {t('dashboard.subtitle')}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.8 }}>
                {t('dashboard.description')}
              </Typography>
            </Paper>

            {/* My Campaigns */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                {t('dashboard.myActiveCampaigns')}
              </Typography>
              {loadingDashboardCampaigns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dashboardCampaigns.length === 0 ? (
                <Alert severity="info">
                  {t('dashboard.noActiveCampaigns')}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {dashboardCampaigns.map((campaign) => (
                    <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                      <Card sx={{ height: '100%' }}>
                        {campaign.campaign_image && (
                          <CardMedia
                            component="img"
                            height="140"
                            image={`${CDN_URL}/${campaign.campaign_image}`}
                            alt={campaign.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                          />
                        )}
                        <CardContent>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {campaign.name}
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                p: 1.5,
                                backgroundColor: 'success.main',
                                color: 'white',
                                borderRadius: 1,
                                textAlign: 'center',
                                mb: 1
                              }}
                            >
                              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                {t('dashboard.availablePoints')}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {campaign.available_points || 0}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.endDate')} {formatDate(campaign.end_date)}
                            </Typography>
                          </Box>

                          {campaign.show_value_1 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                {campaign.type === 'TARGET' ? (
                                  <><strong>{campaign.value_1_sum || 0}</strong> {campaign.value_1_label || 'Value 1'}</>
                                ) : (
                                  <><strong>{campaign.value_1_label || 'Value 1'}:</strong> {campaign.value_1_sum || 0}</>
                                )}
                              </Typography>
                            </Box>
                          )}

                          {campaign.show_value_2 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                {campaign.type === 'TARGET' ? (
                                  <><strong>{campaign.value_2_sum || 0}</strong> {campaign.value_2_label || 'Value 2'}</>
                                ) : (
                                  <><strong>{campaign.value_2_label || 'Value 2'}:</strong> {campaign.value_2_sum || 0}</>
                                )}
                              </Typography>
                            </Box>
                          )}

                          {campaign.show_value_3 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                {campaign.type === 'TARGET' ? (
                                  <><strong>{campaign.value_3_sum || 0}</strong> {campaign.value_3_label || 'Value 3'}</>
                                ) : (
                                  <><strong>{campaign.value_3_label || 'Value 3'}:</strong> {campaign.value_3_sum || 0}</>
                                )}
                              </Typography>
                            </Box>
                          )}

                          <Button
                            variant="contained"
                            size="medium"
                            fullWidth
                            onClick={() => {
                              setSelectedTab('campaigns');
                              handleCampaignDetailOpen(campaign);
                            }}
                            sx={{
                              mt: 2,
                              backgroundColor: companyBrandingColor || '#1976d2',
                              color: 'white',
                              fontWeight: 600,
                              py: 1.5,
                              '&:hover': {
                                backgroundColor: companyBrandingColor ?
                                  `${companyBrandingColor}dd` : '#1565c0'
                              }
                            }}
                          >
                            {t('dashboard.viewDetails')}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {dashboardCampaigns.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedTab('campaigns')}
                    sx={{
                      color: companyBrandingColor || '#1976d2',
                      borderColor: companyBrandingColor || '#1976d2',
                    }}
                  >
                    {t('dashboard.viewAllCampaigns')}
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Marketing Materials */}
            <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                {t('dashboard.marketingMaterials')}
              </Typography>
              {loadingDashboardMarketing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dashboardMarketingItems.length === 0 ? (
                <Alert severity="info">
                  {t('dashboard.noMarketingMaterials')}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {dashboardMarketingItems.slice(0, 3).map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {item.type === 'YOUTUBE' && item.youtube_link ? (
                          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                            <iframe
                              src={item.youtube_link.replace('watch?v=', 'embed/')}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                            />
                          </Box>
                        ) : item.image ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={`${CDN_URL}/${item.image}`}
                            alt={item.title}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                          />
                        ) : null}

                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {item.title}
                          </Typography>

                          {item.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          )}

                          {item.type === 'DOWNLOAD' && item.download_file && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                href={`${CDN_URL}/${item.download_file}`}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  backgroundColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    backgroundColor: companyBrandingColor ?
                                      `${companyBrandingColor}cc` : '#1565c0'
                                  }
                                }}
                              >
                                {t('dashboard.download')}
                              </Button>
                            </Box>
                          )}

                          {item.type === 'YOUTUBE' && item.youtube_link && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                href={item.youtube_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: companyBrandingColor || '#1976d2',
                                  borderColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    borderColor: companyBrandingColor || '#1565c0',
                                    backgroundColor: companyBrandingColor ?
                                      'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                {t('dashboard.watchOnYouTube')}
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {dashboardMarketingItems.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedTab('marketing')}
                    sx={{
                      color: companyBrandingColor || '#1976d2',
                      borderColor: companyBrandingColor || '#1976d2',
                    }}
                  >
                    {t('dashboard.viewAllMarketingMaterials')}
                  </Button>
                </Box>
              )}
            </Paper>
          </Container>
        );

      case 'campaigns':
        if (viewingCampaignDetail) {
          const extraModRel = companyExtraModules?.find(m => m.id === selectedCampaignDetail?.company_extra_module_id);
          const moduleConfig = extraModRel?.config || {};
          const tournamentCampaign = isTournamentCampaign(selectedCampaignDetail, companyExtraModules);
          const showRewards = tournamentCampaign || moduleConfig.show_rewards !== false;
          const rewardsLockedReason = getTournamentRewardsLockedReason({
            isTournament: tournamentCampaign,
            games: moduleConfig.games || [],
            timezone: companyTimezone,
            goal: campaignGoals?.[0],
          });
          const rewardsLocked = rewardsLockedReason !== null;
          return (
            <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <IconButton
                    onClick={handleCampaignDetailClose}
                    sx={{ mr: 2 }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h5" component="div">
                    {t('campaigns.campaignDetails')}
                  </Typography>
                </Box>

                {campaignDetailError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {campaignDetailError}
                  </Alert>
                )}

                {loadingCampaignDetail ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedCampaignDetail ? (
                  <Box sx={{ py: 2 }}>
                    <Grid container spacing={3}>
                      {/* Campaign Image */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          {selectedCampaignDetail.campaign_image ? (
                            <img
                              src={`${CDN_URL}/${selectedCampaignDetail.campaign_image}`}
                              alt={selectedCampaignDetail.name}
                              style={{
                                width: '100%',
                                maxWidth: '300px',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: '300px',
                              height: '200px',
                              display: selectedCampaignDetail.campaign_image ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0',
                              margin: '0 auto'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No Image Available
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Campaign Details */}
                      <Grid item xs={12} md={8}>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                                {selectedCampaignDetail.name}
                              </Typography>

                              {selectedCampaignDetail.header && (
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: companyBrandingColor || '#1976d2' }}>
                                    {selectedCampaignDetail.header}
                                  </Typography>
                                </Box>
                              )}

                              {selectedCampaignDetail.description && (
                                <Box sx={{ mb: 3 }}>
                                  <Box
                                    sx={{
                                      ...quizRichHtmlBlockSx,
                                      '& a': {
                                        color: 'primary.main',
                                        textDecoration: 'underline'
                                      }
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: selectedCampaignDetail.description
                                    }}
                                  />
                                </Box>
                              )}

                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* My Performance Section */}
                      <Grid item xs={12} sx={{ pl: 0 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                          {t('campaigns.myPerformance')}
                        </Typography>

                        {goalsError && (
                          <Alert severity="error" sx={{ mb: 3 }}>
                            {goalsError}
                          </Alert>
                        )}

                        {loadingGoals ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : campaignGoals && campaignGoals.length > 0 ? (
                          <Paper sx={{ p: 3, backgroundColor: '#ffffff' }}>
                            {campaignGoals.map((goal) => (
                              <Box key={goal.id} sx={{ mb: 3 }}>
                                <Grid container spacing={3}>
                                  {/* Points */}
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #4caf50', borderRadius: '8px', backgroundColor: 'success.main' }}>
                                      <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'white' }}>
                                        {goalAvailablePointsFallback(goal)}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                        {selectedCampaignDetail.type === 'EXTRA'
                                          ? t('campaigns.pointsRewardedToDate')
                                          : t('dashboard.availablePoints')}
                                      </Typography>
                                      {selectedCampaignDetail.type !== 'EXTRA' && (
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5 }}>
                                          {t('campaigns.total')} {goalEarnedTotal(goal)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>

                                  {/* Value 1 */}
                                  {selectedCampaignDetail.show_value_1 && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                                          {goal.value_1 || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {selectedCampaignDetail.value_1_label || 'Value 1'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Value 2 */}
                                  {selectedCampaignDetail.show_value_2 && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'success.main' }}>
                                          {goal.value_2 || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {selectedCampaignDetail.value_2_label || 'Value 2'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}

                                  {/* Value 3 */}
                                  {selectedCampaignDetail.show_value_3 && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                          {goal.value_3 || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {selectedCampaignDetail.value_3_label || 'Value 3'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>

                                {/* Status indicators if enabled */}
                                {selectedCampaignDetail.show_status && (
                                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                                      Progress Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                      <Chip
                                        label="Stage 1"
                                        color={goal.done_1 ? 'success' : 'default'}
                                        variant={goal.done_1 ? 'filled' : 'outlined'}
                                        icon={goal.done_1 ? <Box sx={{ fontSize: '14px' }}>✓</Box> : undefined}
                                      />
                                      <Chip
                                        label="Stage 2"
                                        color={goal.done_2 ? 'success' : 'default'}
                                        variant={goal.done_2 ? 'filled' : 'outlined'}
                                        icon={goal.done_2 ? <Box sx={{ fontSize: '14px' }}>✓</Box> : undefined}
                                      />
                                      <Chip
                                        label="Stage 3"
                                        color={goal.done_3 ? 'success' : 'default'}
                                        variant={goal.done_3 ? 'filled' : 'outlined'}
                                        icon={goal.done_3 ? <Box sx={{ fontSize: '14px' }}>✓</Box> : undefined}
                                      />
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Paper>
                        ) : (
                          <Alert severity="info">
                            No performance data available for this campaign.
                          </Alert>
                        )}
                      </Grid>

                      {tournamentCampaign && (
                        <PastGamesSection
                          moduleConfig={moduleConfig}
                          campaignGoals={campaignGoals}
                          companyTimezone={companyTimezone}
                        />
                      )}

                      {/* Match Predictor Section */}
                      {selectedCampaignDetail.type === 'EXTRA' && selectedCampaignDetail.company_extra_module_id && (
                        <MatchPredictorSection
                          moduleConfig={moduleConfig}
                          campaignGoals={campaignGoals}
                          selectedCampaignDetail={selectedCampaignDetail}
                          apiUrl={API_URL}
                          loadCampaignGoals={loadCampaignGoals}
                          companyTimezone={companyTimezone}
                        />
                      )}

                      {/* Quizzes Section - only show when campaign has quizzes */}
                      {(loadingCampaignQuizzes || (campaignQuizzes && campaignQuizzes.length > 0)) && (
                        <Grid item xs={12} sx={{ pl: 0, overflow: 'visible' }}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            {t('campaigns.quizzes')}
                          </Typography>

                          {campaignQuizzesError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                              {campaignQuizzesError}
                            </Alert>
                          )}

                          {loadingCampaignQuizzes ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : campaignQuizzes && campaignQuizzes.length > 0 ? (
                            <Grid container spacing={2} sx={{ overflow: 'visible' }}>
                              {campaignQuizzes.map((quiz) => {
                                const hasAttempt = quiz.user_attempt !== null && quiz.user_attempt !== undefined;
                                const isCompleted = hasAttempt && quiz.user_attempt.completed;
                                const isPerfect = isCompleted && quiz.user_attempt.score === quiz.user_attempt.total_questions;
                                const attemptCount = quiz.attempt_count || 0;
                                const singleCampaignQuiz = campaignQuizzes.length === 1;
                                const salesRewardDisclaimer = showQuizSalesRewardDisclaimer(quiz);

                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={singleCampaignQuiz ? 12 : 6}
                                    md={singleCampaignQuiz ? 12 : 4}
                                    key={quiz.id}
                                    sx={{ alignSelf: 'flex-start', width: '100%', overflow: 'visible' }}
                                  >
                                    {salesRewardDisclaimer && (
                                      <Alert severity="info" sx={pointsLockedAlertSx}>
                                        {t('quiz.pointsRequiredDisclaimer')}
                                      </Alert>
                                    )}
                                    <Card
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'visible',
                                        width: '100%',
                                      }}
                                    >
                                      <CardContent sx={{ flex: '1 1 auto', minHeight: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                          <Typography variant="h6" component="div" sx={{ fontWeight: 600, flexGrow: 1 }} {...quizNoTranslateProps}>
                                            {quiz.title}
                                          </Typography>
                                          {isPerfect && (
                                            <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                                          )}
                                        </Box>

                                        {getQuizPreviewHtml(quiz) && (
                                          <Box
                                            {...quizNoTranslateProps}
                                            sx={quizRichContentSx}
                                            dangerouslySetInnerHTML={{ __html: getQuizPreviewHtml(quiz) }}
                                          />
                                        )}

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                          <Chip
                                            label={`${quiz.questions?.length || 0} ${t('campaigns.questions')}`}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Box>

                                        {/* Score and Status Info */}
                                        <Box sx={{ mt: 2 }}>
                                          {isCompleted && quiz.user_attempt && (
                                            <Box sx={{ mb: 1 }}>
                                              <Chip
                                                label={`${t('campaigns.score')} ${quiz.user_attempt.score}/${quiz.user_attempt.total_questions}`}
                                                size="small"
                                                color={isPerfect ? "success" : "warning"}
                                              />
                                            </Box>
                                          )}

                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {t('campaigns.attempts')} {attemptCount}/3
                                          </Typography>

                                          {getQuizRewardLabel(quiz, t, 'campaigns') && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                              {getQuizRewardLabel(quiz, t, 'campaigns')}
                                            </Typography>
                                          )}

                                          {!hasAttempt && (
                                            <Chip
                                              label={t('campaigns.notStarted')}
                                              size="small"
                                              variant="outlined"
                                            />
                                          )}
                                        </Box>
                                      </CardContent>
                                      {!isPerfect && (
                                        <CardActions sx={{ flexShrink: 0, px: 2, pb: 2, pt: 0 }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handleStartQuiz(quiz)}
                                            disabled={attemptCount >= 3}
                                            sx={{
                                              backgroundColor: companyBrandingColor || '#1976d2',
                                              '&:hover': {
                                                backgroundColor: companyBrandingColor || '#1565c0',
                                              },
                                            }}
                                          >
                                            {hasAttempt ? t('campaigns.retakeQuiz') : t('campaigns.takeQuiz')}
                                          </Button>
                                        </CardActions>
                                      )}
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          ) : (
                            <Alert severity="info">
                              {t('campaigns.noQuizzesAvailable')}
                            </Alert>
                          )}
                        </Grid>
                      )}

                      {/* Rewards, Pending Orders, and My Vouchers Sections — nested Grid container (not Fragment) so layout does not overlap quizzes */}
                      <Grid item xs={12} sx={{ width: '100%', overflow: 'visible' }}>
                        <RewardsSection
                          selectedCampaignDetail={selectedCampaignDetail}
                          showRewards={showRewards}
                          rewardsLocked={rewardsLocked}
                          rewardsLockedReason={rewardsLockedReason}
                          CDN_URL={CDN_URL}
                          t={t}
                          formatDateTime={formatDateTime}
                          cart={cart} cartDenominations={cartDenominations} setCartDenominations={setCartDenominations}
                          addToCart={addToCart} removeFromCart={removeFromCart} getCartTotal={getCartTotal} clearCart={clearCart}
                          getTotalItemsInCart={getTotalItemsInCart}
                          edenredCart={edenredCart} edenredCustomValues={edenredCustomValues} setEdenredCustomValues={setEdenredCustomValues}
                          getEdenredCartTotal={getEdenredCartTotal}
                          calculateEdenredTransferPoints={calculateEdenredTransferPoints}
                          tremendousCustomValues={tremendousCustomValues} setTremendousCustomValues={setTremendousCustomValues}
                          requestingTremendousVoucher={requestingTremendousVoucher}
                          handleRequestTremendousVoucherClick={handleRequestTremendousVoucherClick}
                          requestingVouchers={requestingVouchers} handleRequestVouchersClick={handleRequestVouchersClick}
                          handleTransferMoneyClick={handleTransferMoneyClick}
                          voucherItems={voucherItems} loadingVoucherItems={loadingVoucherItems} voucherItemsError={voucherItemsError}
                          pendingVoucherRequests={pendingVoucherRequests}
                          userRewardProviderConfig={userRewardProviderConfig}
                          setUserRewardProviderConfig={setUserRewardProviderConfig}
                          loadingUserConfig={loadingUserConfig}
                          selectedSerialNumber={selectedSerialNumber} setSelectedSerialNumber={setSelectedSerialNumber}
                          requestingCard={requestingCard} setRequestingCard={setRequestingCard}
                          getAvailablePoints={getAvailablePoints}
                          apiCall={apiCall}
                          showFeedback={showFeedback}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : null}
              </Paper>
            </Container>
          );
        }

        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                {t('campaigns.myCampaigns')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('campaigns.description')}
              </Typography>

              {campaignsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {campaignsError}
                </Alert>
              )}

              {loadingCampaigns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : campaigns.length === 0 ? (
                <Alert severity="info">
                  {t('campaigns.noActiveCampaigns')}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: '#ffffff' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.image')}</TableCell>
                          <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.campaignName')}</TableCell>
                          <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.endDate')}</TableCell>
                          <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.availablePoints')}</TableCell>
                          {campaignGoalSettings?.show_value_1 && (
                            <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.sales')}</TableCell>
                          )}
                          {campaignGoalSettings?.show_value_2 && (
                            <TableCell sx={{ py: 2, fontWeight: 600 }}></TableCell>
                          )}
                          {campaignGoalSettings?.show_value_3 && (
                            <TableCell sx={{ py: 2, fontWeight: 600 }}></TableCell>
                          )}
                          <TableCell sx={{ py: 2, fontWeight: 600 }}>{t('campaigns.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {campaigns.map((campaign) => (
                          <TableRow
                            key={campaign.id}
                            onClick={() => handleCampaignDetailOpen(campaign)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Box sx={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                {campaign.campaign_image ? (
                                  <img
                                    src={`${CDN_URL}/${campaign.campaign_image}`}
                                    alt={campaign.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      borderRadius: '4px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: campaign.campaign_image ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {campaign.name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              {campaign.end_date ? (
                                <Typography variant="body1">
                                  {formatDate(campaign.end_date)}
                                </Typography>
                              ) : <Typography variant="body1">{t('campaigns.noEndDate')}</Typography>}
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Box sx={{
                                textAlign: 'center',
                                p: 1.5,
                                border: '1px solid #4caf50',
                                borderRadius: '8px',
                                backgroundColor: 'success.main'
                              }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                                  {campaign.available_points || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  Points
                                </Typography>
                              </Box>
                            </TableCell>
                            {campaignGoalSettings?.show_value_1 && (
                              <TableCell sx={{ py: 2 }}>
                                {campaign.show_value_1 ? (
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#f8f9fa'
                                  }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                                      {campaign.value_1_sum || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                      {campaign.value_1_label || 'Value 1'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body1" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            {campaignGoalSettings?.show_value_2 && (
                              <TableCell sx={{ py: 2 }}>
                                {campaign.show_value_2 ? (
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#f8f9fa'
                                  }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                      {campaign.value_2_sum || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                      {campaign.value_2_label || 'Value 2'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body1" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            {campaignGoalSettings?.show_value_3 && (
                              <TableCell sx={{ py: 2 }}>
                                {campaign.show_value_3 ? (
                                  <Box sx={{
                                    textAlign: 'center',
                                    p: 1.5,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#f8f9fa'
                                  }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                      {campaign.value_3_sum || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                      {campaign.value_3_label || 'Value 3'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body1" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleCampaignDetailOpen(campaign)}
                                sx={{
                                  color: companyBrandingColor || '#1976d2'
                                }}
                                title={t('dashboard.viewDetails')}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={campaignsTotal}
                    rowsPerPage={campaignsRowsPerPage}
                    page={campaignsPage}
                    onPageChange={handleCampaignsPageChange}
                    onRowsPerPageChange={handleCampaignsRowsPerPageChange}
                  />
                </>
              )}
            </Paper>
          </Container>
        );

      case 'leaderboards':
        return (
          <LeaderboardsSection
            campaigns={campaignsWithLeaderboards}
            leaderboardGoals={leaderboardGoals}
            loadingLeaderboardCampaigns={loadingLeaderboardCampaigns}
            loadingLeaderboardGoals={loadingLeaderboardGoals}
            leaderboardSort={leaderboardSort}
            handleLeaderboardSortChange={handleLeaderboardSortChange}
            getSortedLeaderboardGoals={getSortedLeaderboardGoals}
            companyExtraModules={companyExtraModules}
            highlightDealerId={user?.dealer_id}
            labels={{
              title: t('leaderboards.title'),
              description: t('leaderboards.description'),
              noLeaderboards: t('leaderboards.noLeaderboards'),
              dealerName: t('leaderboards.dealerName'),
              teamName: t('leaderboards.teamName'),
              rank: t('leaderboards.rank'),
              points: t('leaderboards.points'),
              status: t('leaderboards.status'),
              unknownDealer: t('leaderboards.unknownDealer'),
              noPerformanceData: t('leaderboards.noPerformanceData'),
            }}
          />
        );

      case 'bonusTask':
        // Call as function so the editor is not remounted on every keystroke
        // (inner component identity would change each Dealer render).
        return BonusTaskView();

      case 'quiz':
        return QuizView();

      case 'marketing':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
              <Typography variant="h5" gutterBottom>
                {t('dashboard.marketingMaterials')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('marketing.description')}
              </Typography>

              {marketingItemsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {marketingItemsError}
                </Alert>
              )}

              {loadingMarketingItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : marketingItems.length === 0 ? (
                <Alert severity="info">
                  {t('marketing.noMaterials')}
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {marketingItems.map((item) => (
                    <Grid item xs={12} md={6} lg={4} key={item.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {item.type === 'YOUTUBE' && item.youtube_link ? (
                          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                            <iframe
                              src={item.youtube_link.replace('watch?v=', 'embed/')}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                            />
                          </Box>
                        ) : item.image ? (
                          <CardMedia
                            component="img"
                            height="200"
                            image={`${CDN_URL}/${item.image}`}
                            alt={item.title}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                          />
                        ) : null}

                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {item.title}
                          </Typography>

                          {item.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          )}

                          {item.type === 'DOWNLOAD' && item.download_file && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                href={`${CDN_URL}/${item.download_file}`}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  backgroundColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    backgroundColor: companyBrandingColor ?
                                      `${companyBrandingColor}cc` : '#1565c0'
                                  }
                                }}
                              >
                                {t('dashboard.download')}
                              </Button>
                            </Box>
                          )}

                          {item.type === 'YOUTUBE' && item.youtube_link && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="outlined"
                                href={item.youtube_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: companyBrandingColor || '#1976d2',
                                  borderColor: companyBrandingColor || '#1976d2',
                                  '&:hover': {
                                    borderColor: companyBrandingColor || '#1565c0',
                                    backgroundColor: companyBrandingColor ?
                                      'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                {t('dashboard.watchOnYouTube')}
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Container>
        );

      case 'settings':
        return (
          <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                {t('settings.title')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon />
                        {t('settings.password')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('settings.changePasswordDescription')}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        onClick={() => setShowPasswordChangeDialog(true)}
                      >
                        {t('settings.changePassword')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Container>
        );

      default:
        return null;
    }
  };

  const handleFormalDocumentsConfirm = async ({ tournamentNickname } = {}) => {
    const body = formalDocumentsData.hasTournamentModule
      ? { tournament_nickname: tournamentNickname ?? '' }
      : {};
    const result = await apiCall('post', '/api/dealer/formal-documents-accept', body);
    if (result.success) {
      setShowFormalDocumentsDialog(false);
    }
  };

  const handleFormalDocumentsLogout = () => {
    logout();
    navigate('/login');
  };

  // Show loading while authentication is being checked or formal documents status for dealers
  if (loading || (user?.role === 'Dealer' && loadingFormalDocumentsCheck)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: companyBrandingColor || 'default',
            color: companyBrandingColor ? 'white' : 'inherit',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
          {/* Company Branding Image */}
          {companyBrandingImage && !isNarrow && (
            <Box sx={{
              pt: 2,
              pb: 2,
              px: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderBottom: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <img
                src={companyBrandingImage}
                alt={t('layout.companyLogo')}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}

          {companyBrandingImage && isNarrow && (
            <Box sx={{
              pt: 1,
              pb: 1,
              px: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderBottom: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              <img
                src={companyBrandingImage}
                alt={t('layout.companyLogo')}
                style={{
                  maxWidth: '40px',
                  maxHeight: '40px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={item.key !== 'dealerPortal' && selectedTab === item.key}
                  onClick={() => {
                    if (item.key === 'dealerPortal' && dealerPortalUrl) {
                      window.open(dealerPortalUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      handleMenuItemClick(item.key);
                    }
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNarrow ? 'center' : 'initial',
                    px: 2.5,
                    color: companyBrandingColor ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.12)',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isNarrow ? 'auto' : 3,
                      justifyContent: 'center',
                      color: companyBrandingColor ? 'white' : 'inherit',
                    }}
                  >
                    <item.icon />
                  </ListItemIcon>
                  {!isNarrow && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* User info and logout */}
        <Box sx={{
          mt: 'auto',
          borderTop: companyBrandingColor ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          {!isNarrow && (formalDocumentsData.terms || formalDocumentsData.privacy || companySupportEmail) && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              {formalDocumentsData.terms && (
                <Link
                  href={formalDocumentsData.terms.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    color: companyBrandingColor ? 'rgba(255, 255, 255, 0.9)' : 'primary.main',
                    textDecoration: 'none',
                    mb: 0.5,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {t('formalDocuments.termsLabel')}
                </Link>
              )}
              {formalDocumentsData.privacy && (
                <Link
                  href={formalDocumentsData.privacy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    color: companyBrandingColor ? 'rgba(255, 255, 255, 0.9)' : 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {t('formalDocuments.privacyLabel')}
                </Link>
              )}
              {companySupportEmail && (
                <Typography
                  component="div"
                  sx={{
                    fontSize: '0.75rem',
                    color: companyBrandingColor ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                    mt: 0.25
                  }}
                >
                  {t('layout.supportLabel')}{' '}
                  <Link
                    href={`mailto:${companySupportEmail}`}
                    sx={{
                      color: companyBrandingColor ? 'rgba(255, 255, 255, 0.9)' : 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {companySupportEmail}
                  </Link>
                </Typography>
              )}
            </Box>
          )}
          {!isNarrow && user && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" sx={{
                color: companyBrandingColor ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                display: 'block'
              }}>
                {t('layout.welcomeUser', { name: isImpersonating ? originalUser?.name : user.name })}
              </Typography>
              {user.dealer && (
                <Typography variant="caption" sx={{
                  color: companyBrandingColor ? 'rgba(255, 255, 255, 0.6)' : 'text.secondary',
                  display: 'block'
                }}>
                  {user.dealer.name}
                </Typography>
              )}
            </Box>
          )}
          {isImpersonating && (
            <Box sx={{
              mx: 2,
              mb: 2,
              p: 1,
              backgroundColor: 'warning.main',
              borderRadius: 1,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{
                color: 'warning.contrastText',
                fontWeight: 'bold'
              }}>
                {t('layout.impersonating', { name: user?.name })}
              </Typography>
              {!isNarrow && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={async () => {
                      const result = await stopImpersonation();
                      if (result.success) {
                        navigate('/admin');
                      }
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      minHeight: 'auto',
                      py: 0.5,
                      px: 1
                    }}
                  >
                    {t('menu.stopImpersonating')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: isNarrow ? 'center' : 'flex-end', px: 2, py: 1 }}>
              <IconButton
                onClick={toggleSidebar}
                size="small"
                aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                sx={{ color: companyBrandingColor ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
              >
                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
          )}
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout} sx={{
              color: companyBrandingColor ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: companyBrandingColor ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}>
              <ListItemIcon sx={{
                minWidth: 0,
                mr: isNarrow ? 'auto' : 3,
                justifyContent: 'center',
                color: companyBrandingColor ? 'white' : 'inherit',
              }}>
                <LogoutIcon />
              </ListItemIcon>
              {!isNarrow && <ListItemText primary={t('menu.logout')} />}
            </ListItemButton>
          </ListItem>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        {renderContent()}
        <PasswordChangeDialog
          open={showPasswordChangeDialog}
          onClose={() => setShowPasswordChangeDialog(false)}
        />

        <FormalDocumentsAcceptanceDialog
          open={showFormalDocumentsDialog}
          terms={formalDocumentsData.terms}
          privacy={formalDocumentsData.privacy}
          extra={formalDocumentsData.extra}
          requireTournamentNickname={formalDocumentsData.hasTournamentModule === true}
          onConfirm={handleFormalDocumentsConfirm}
          onLogout={handleFormalDocumentsLogout}
          t={t}
        />

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmationDialog}
          onClose={() => setShowConfirmationDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 3 }}>
              <CreditCardIcon
                sx={{
                  fontSize: 80,
                  color: 'primary.main',
                  filter: 'drop-shadow(0 4px 8px rgba(25, 118, 210, 0.3))'
                }}
              />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2
              }}
            >
              {confirmationAction === 'tremendous' || confirmationAction === 'cart' ? t('dialogs.confirmVoucherRequest') : t('dialogs.confirmMoneyTransfer')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              {confirmationAction === 'tremendous'
                ? t('dialogs.confirmVoucherSingle')
                : confirmationAction === 'cart'
                  ? t('dialogs.confirmVouchersCart')
                  : t('dialogs.confirmTransfer')}
            </Typography>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: 'grey.100',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                {confirmationAction === 'tremendous' || confirmationAction === 'cart' ? t('dialogs.pointsToUse') : t('dialogs.pointsToTransfer')}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                {confirmationPoints.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dialogs.available')} {getAvailablePoints().toLocaleString()} {t('campaigns.points')}
              </Typography>
              {confirmationPoints > getAvailablePoints() && (
                <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                  {t('dialogs.insufficientPoints')}
                </Typography>
              )}
            </Paper>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {confirmationAction === 'tremendous'
                ? t('dialogs.confirmDeductPoints')
                : confirmationAction === 'cart'
                  ? t('dialogs.confirmRequestVouchers')
                  : t('dialogs.confirmTransferMoney')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button
              onClick={() => setShowConfirmationDialog(false)}
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: 'grey.400',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.50',
                },
              }}
            >
              {t('dialogs.cancel')}
            </Button>
            <Button
              onClick={handleConfirmedAction}
              variant="contained"
              size="large"
              disabled={confirmationPoints > getAvailablePoints()}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: companyBrandingColor || 'primary.main',
                '&:hover': {
                  backgroundColor: companyBrandingColor || 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'grey.300',
                  color: 'grey.500',
                },
              }}
            >
              {t('dialogs.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Voucher Success Dialog */}
        <Dialog
          open={showVoucherSuccessDialog}
          onClose={() => setShowVoucherSuccessDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 3 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))'
                }}
              />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: 'success.main',
                mb: 2
              }}
            >
              {t('dialogs.voucherSubmitted')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 1.5, lineHeight: 1.6 }}
            >
              {t('dialogs.voucherProcessed')}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {t('dialogs.viewInMyVouchers')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              onClick={() => setShowVoucherSuccessDialog(false)}
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                backgroundColor: companyBrandingColor || 'success.main',
                '&:hover': {
                  backgroundColor: companyBrandingColor || 'success.dark',
                },
              }}
            >
              {t('dialogs.gotIt')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success / error feedback (replaces browser alert for rewards flows) */}
        <Dialog
          open={feedbackDialog.open}
          onClose={closeFeedbackDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
              overflow: 'hidden',
            },
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <Box sx={{ mb: 2.5 }}>
              {feedbackDialog.variant === 'success' ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: 76,
                    color: 'success.main',
                    filter: 'drop-shadow(0 4px 12px rgba(76, 175, 80, 0.35))',
                  }}
                />
              ) : (
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 76,
                    color: 'error.main',
                    filter: 'drop-shadow(0 4px 12px rgba(211, 47, 47, 0.3))',
                  }}
                />
              )}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: feedbackDialog.message ? 2 : 0,
                lineHeight: 1.3,
                color: feedbackDialog.variant === 'success' ? 'success.main' : 'error.main',
              }}
            >
              {feedbackDialog.title}
            </Typography>
            {feedbackDialog.message ? (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 280,
                  overflow: 'auto',
                  textAlign: 'center',
                }}
              >
                {feedbackDialog.message}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0, px: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={closeFeedbackDialog}
              sx={{
                px: 5,
                py: 1.25,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                ...(feedbackDialog.variant === 'success'
                  ? {
                      backgroundColor: companyBrandingColor || 'success.main',
                      '&:hover': {
                        backgroundColor: companyBrandingColor
                          ? `${companyBrandingColor}dd`
                          : 'success.dark',
                      },
                    }
                  : {
                      backgroundColor: 'error.main',
                      '&:hover': { backgroundColor: 'error.dark' },
                    }),
              }}
            >
              {t('dialogs.gotIt')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tremendous Reward Success Dialog */}
        <Dialog
          open={showTremendousRewardSuccessDialog}
          onClose={() => {
            setShowTremendousRewardSuccessDialog(false);
            setTremendousRewardLink(null);
            setTremendousRewardInfo(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 3 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))'
                }}
              />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: 'success.main',
                mb: 2
              }}
            >
              {t('dialogs.rewardReady')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              {t('dialogs.tremendousSuccess')}
            </Typography>

            {tremendousRewardInfo && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('dialogs.rewardDetails')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {tremendousRewardInfo.rewardName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dialogs.amount')} {formatCurrency(tremendousRewardInfo.amount, tremendousRewardInfo.currency || 'USD')}
                </Typography>
              </Paper>
            )}

            {tremendousRewardLink && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => window.open(tremendousRewardLink, '_blank')}
                  startIcon={<OpenInNewIcon />}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: companyBrandingColor || 'primary.main',
                    '&:hover': {
                      backgroundColor: companyBrandingColor || 'primary.dark',
                    },
                  }}
                >
                  {t('dialogs.openRewardLink')}
                </Button>
              </Box>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, fontStyle: 'italic' }}
            >
              {t('dialogs.alsoInMyVouchers')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              onClick={() => {
                setShowTremendousRewardSuccessDialog(false);
                setTremendousRewardLink(null);
                setTremendousRewardInfo(null);
              }}
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              {t('dialogs.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Dealer; 